// The one collapse/expand chevron. It owns BOTH axes a disclosure glyph turns on
// — open state and text direction — because they interact, which is why they kept
// getting written apart.
//
// The house idiom for a directional icon is a bare `rtl:rotate-180` (~150 sites).
// That is exactly what a disclosure chevron cannot use: it already spends its
// transform on the open state, so `cn('rotate-90', 'rtl:rotate-180')` emits BOTH
// classes and the RTL one wins in the OPEN state too — the glyph points left
// instead of down.
//
// It wins on SOURCE ORDER, not specificity, and the difference matters if you
// ever try to "fix" this by reordering the arguments. Compiled (Tailwind 3.4):
//
//   .rotate-90                                        { --tw-rotate:  90deg }
//   .rtl\:rotate-180:where([dir="rtl"], [dir="rtl"] *) { --tw-rotate: 180deg }
//
// `:where()` contributes ZERO specificity by definition — that is why Tailwind
// wraps variants in it — so both selectors are (0,1,0) and the later one simply
// wins. `cn()` cannot help either: tailwind-merge keeps both, because a variant
// and its unprefixed sibling are not the same utility.
//
// So the two rotations are mutually exclusive BRANCHES here, resolved once, and
// only one class is ever emitted.
//
//   closed · LTR   0°   ▶ points right, into the content it reveals
//   closed · RTL   180° ◀ points left, into the content it reveals
//   open  · either 90°  ▼ points down, at the revealed content
//
// The alternative spelling — swapping ChevronDown for ChevronRight on open — is
// what several surfaces hand-rolled, and it is worse twice over: it cannot animate
// (two different glyphs), and its collapsed arm is a raw ChevronRight that no
// amount of `rtl:` on the wrapper can mirror without also mirroring the open arm.
import { ChevronRight } from 'lucide-react';
import { cn } from '../../../lib/utils';

interface DisclosureChevronProps {
  /**
   * Whether the section this chevron labels is expanded.
   *
   * Pass the string `'group'` for an UNCONTROLLED Radix `Collapsible`, where the
   * open state exists only as `data-state` on the trigger and there is no React
   * boolean to read — the trigger carries the `group` class and the chevron
   * resolves both branches off `group-data-[state=…]`. The two branches key on
   * OPPOSITE states, so neither can shadow the other whatever order Tailwind
   * emits them in.
   */
  open: boolean | 'group';
  /** Additional className — size, color, opacity. Overrides the defaults via `cn`. */
  className?: string;
}

/**
 * Collapse/expand affordance for a disclosure trigger. Decorative by
 * construction: the trigger it sits in carries `aria-expanded`, so the glyph is
 * `aria-hidden` and adds nothing to the accessible name.
 */
export function DisclosureChevron({ open, className }: DisclosureChevronProps) {
  return (
    <ChevronRight
      aria-hidden="true"
      className={cn(
        'h-4 w-4 shrink-0 transition-transform duration-200',
        open === 'group'
          ? 'group-data-[state=open]:rotate-90 rtl:group-data-[state=closed]:rotate-180'
          : open
            ? 'rotate-90'
            : 'rtl:rotate-180',
        className
      )}
    />
  );
}
